import type { TeamWith } from "@redotech/account-common/base-team";
import { InventoryProjectionStatus } from "@redotech/crdb-enums/inventory/inventory-projection-status";
import { sortFulfillmentOrderLineItems } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  expandLineItemsToPickableItems,
  FulfillmentOrderLineItem,
  LineItemLot,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { inventoryProjectionStatusLabels } from "@redotech/redo-fulfillment-operations-common/inventory-projection-status-labels";
import { ItemOrderBy } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import {
  ItemTableHeader,
  ProductListRow,
} from "@redotech/redo-web/product-list-row";
import { memo, useMemo } from "react";
import { LineItemLots } from "./line-item-lots";
import { useProductPageHrefs } from "./product-page-hrefs";

interface ShortBadge {
  productId: string;
  quantity: number;
  /** Set when a bundle line is short because of this component. */
  component?: string;
}

/**
 * Each row's shortages: its own product's, shown once when the product spans
 * several rows, and for a bundle, each short component's.
 */
function getShortBadges(
  items: readonly FulfillmentOrderLineItem[],
  shortQuantityByProductId: ReadonlyMap<string, number> | undefined,
): ShortBadge[][] {
  if (!shortQuantityByProductId?.size) {
    return items.map(() => []);
  }
  const shownProductIds = new Set<string>();
  return items.map((item) => {
    const badges: ShortBadge[] = [];
    const leaves = expandLineItemsToPickableItems([item], {
      hideInputItems: false,
    });
    for (const leaf of leaves) {
      const productId = leaf.redoProductId;
      const quantity = productId && shortQuantityByProductId.get(productId);
      if (
        !productId ||
        !quantity ||
        badges.some((b) => b.productId === productId)
      ) {
        continue;
      }
      if (productId !== item.redoProductId) {
        badges.push({
          productId,
          quantity,
          component: leaf.sku || leaf.productTitle || leaf.title,
        });
      } else if (!shownProductIds.has(productId)) {
        shownProductIds.add(productId);
        badges.push({ productId, quantity });
      }
    }
    return badges;
  });
}

export const ItemsTable = memo(function ItemsTable({
  items,
  team,
  disableTooltip = false,
  hideHeader = false,
  showBin = false,
  linkToProductPages = false,
  shortQuantityByProductId,
}: {
  items: (FulfillmentOrderLineItem & { lots?: LineItemLot[] })[];
  team: TeamWith<{ outboundLabels: OutboundLabelSettings }> | undefined;
  disableTooltip?: boolean;
  hideHeader?: boolean;
  showBin?: boolean;
  linkToProductPages?: boolean;
  /** Projected units short, by Redo product id. */
  shortQuantityByProductId?: ReadonlyMap<string, number>;
}) {
  const sortedItems = useMemo(() => {
    return sortFulfillmentOrderLineItems(
      team?.settings?.outboundLabels?.documents?.pickList?.orderBy ??
        ItemOrderBy.QuantityDesc,
      items,
    ).filter((item) => item.quantity > 0);
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const shortBadges = useMemo(
    () => getShortBadges(sortedItems, shortQuantityByProductId),
    [sortedItems, shortQuantityByProductId],
  );

  const productHrefs = useProductPageHrefs(
    linkToProductPages ? sortedItems.map((item) => item.redoProductId) : [],
  );

  const showCustomAttributes =
    !!team?.settings?.outboundLabels?.degroupItemsByCustomAttributes;
  const hideUnderscoreCustomAttributes =
    !!team?.settings?.outboundLabels?.hideUnderscoreCustomAttributes;

  return (
    <Flex
      borderColor="primary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="none"
    >
      {!hideHeader && (
        <ItemTableHeader flatTopCorners={false} showBin={showBin} />
      )}
      <Flex dir="column" flex="1" gap="none">
        {sortedItems.map((item, index) => {
          const badges = shortBadges[index] ?? [];
          return (
            <Flex
              borderBottomWidth={index !== items.length - 1 ? "1px" : "none"}
              borderColor="secondary"
              borderStyle="solid"
              dir="column"
              gap="none"
              key={index}
              w="full"
            >
              <ProductListRow
                bin={showBin ? item.bin : undefined}
                customAttributes={
                  showCustomAttributes
                    ? hideUnderscoreCustomAttributes
                      ? item.customAttributes?.filter(
                          (attr) => !attr.key.startsWith("_"),
                        )
                      : item.customAttributes
                    : undefined
                }
                disableTooltip={disableTooltip}
                imageUrl={item.imageUrl}
                price={item.unitDiscountedPrice.amount}
                priceMoney={toMoney(item.unitDiscountedPrice)}
                productHref={
                  item.redoProductId
                    ? productHrefs.get(item.redoProductId)
                    : undefined
                }
                quantity={item.quantity}
                sku={item.sku}
                title={item.productTitle || item.title}
                variantTitle={item.variantTitle}
              />
              {badges.length > 0 || item.lots?.length ? (
                <Flex
                  align="flex-start"
                  dir="column"
                  gap="xs"
                  pb="lg"
                  px="xl"
                  w="full"
                >
                  {badges.map((badge) => (
                    <RedoBadge
                      color="error"
                      key={badge.productId}
                      size="xs"
                      text={[
                        `${inventoryProjectionStatusLabels[InventoryProjectionStatus.Short]} ${badge.quantity}`,
                        badge.component,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    />
                  ))}
                  <LineItemLots lots={item.lots} />
                </Flex>
              ) : null}
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
});
